
import { Label, LabelFilter } from '@/model';
import IconButton from '@/components/IconButton.vue';
import LabelFilterEditor from '@/components/LabelFilterEditor.vue';
import LabelEditorDialog from '@/components/LabelEditorDialog.vue';
import LabelChip from '@/components/LabelChip.vue';
import { objectToArray } from '@/util/array-util';
import labelsStore, { searchLabels } from '@/store/labels';
import Vue from 'vue';

export default Vue.extend({
  name: 'LabelsView',

  components: {
    IconButton,
    LabelFilterEditor,
    LabelEditorDialog,
    LabelChip,
  },

  props: {
    loading: { type: Boolean, default: false },
    labels: { type: Array as () => Label[], default: () => [] },
    filter: { type: Object as () => LabelFilter },
    usageCounts: { type: Object as () => Record<number, number>, default: () => ({}) },
  },

  data() {
    return {
      showEditor: false,
      editItem: undefined as Label | undefined,
      editFilter: {} as LabelFilter,
      searchTimeout: null as ReturnType<typeof setTimeout> | null,
      itemCount: 0,
      headers: [
        { text: 'Id', align: 'start', value: 'id' },
        { text: 'Type', value: 'type' },
        { text: 'Preview', value: 'preview', sortable: false },
        { text: 'Description', value: 'description' },
        { text: 'External ID', value: 'externalId' },
        { text: 'Usage', value: 'usage' },
        { text: 'Actions', value: 'actions', sortable: false },
        {
          text: 'Relations', value: 'data-table-expand', sortable: false, width: '100px',
        },
      ],
    };
  },

  computed: {
    filteredLabels(): Label[] {
      const f = this.editFilter;

      let result = f.search
        ? searchLabels(this.labels, f.search, {
          includeDescription: true, caseSensitive: false, specialChars: true, fuzzy: true,
        })
        : this.labels;

      if (f.types && f.types.length > 0) {
        const types = f.types;
        result = result.filter((l) => types.includes(l.type));
      }

      if (f.selectableOnly) {
        result = result.filter((l) => l.selectable);
      }

      if (f.hasExternalId) {
        result = result.filter((l) => !!l.externalId);
      }

      return result;
    },
    isFilterEmpty(): boolean {
      return objectToArray(this.editFilter).every((v) => v === undefined || v === false || (Array.isArray(v) && v.length === 0));
    },
  },

  watch: {
    filter: {
      handler(v: LabelFilter) {
        this.editFilter = { ...v };
      },
      immediate: true,
    },
  },

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },

  methods: {
    getParentLabels(label: Label): Label[] {
      const edges = labelsStore.state.edges;
      const lookup = labelsStore.getters.lookup;
      return edges
        .filter((e) => e.toLabelId === label.id)
        .map((e) => lookup.get(e.fromLabelId))
        .filter((l): l is Label => l !== undefined);
    },
    getChildLabels(label: Label): Label[] {
      const edges = labelsStore.state.edges;
      const lookup = labelsStore.getters.lookup;
      return edges
        .filter((e) => e.fromLabelId === label.id)
        .map((e) => lookup.get(e.toLabelId))
        .filter((l): l is Label => l !== undefined);
    },
    getRelationCount(label: Label): number {
      return this.getParentLabels(label).length + this.getChildLabels(label).length;
    },
    newLabel() {
      this.editItem = undefined;
      this.showEditor = true;
    },
    editLabel(label: Label) {
      this.editItem = { ...label };
      this.showEditor = true;
    },
    toggleSelectable(label: Label) {
      this.$emit('toggleSelectable', label);
    },
    onSaved(label: Label) {
      this.$emit('saved', label);
    },
    changeFilter(value: LabelFilter) {
      this.editFilter = { ...value };
      this.$emit('filterChange', this.editFilter);
    },
    onSearchInput(value: string) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.editFilter = { ...this.editFilter, search: value || undefined };
        this.$emit('filterChange', this.editFilter);
      }, 300);
    },
    resetFilter() {
      this.$emit('resetFilter');
    },
    customSort(items: Label[], sortBy: string[], sortDesc: boolean[]): Label[] {
      if (sortBy.length === 0) return items;

      const key = sortBy[0];
      const desc = sortDesc[0];

      return [...items].sort((a, b) => {
        let aVal: any;
        let bVal: any;

        if (key === 'usage') {
          aVal = this.usageCounts[a.id] || 0;
          bVal = this.usageCounts[b.id] || 0;
        } else {
          aVal = (a as any)[key];
          bVal = (b as any)[key];
        }

        if (aVal === bVal) return 0;
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        const result = aVal < bVal ? -1 : 1;
        return desc ? -result : result;
      });
    },
  },
});
